// EXPERIÊNCIA — Detalhe do Pedido (página NOVA, /payments/order/:id/nova).
// Síntese ("meio comum") das 6 variantes do projeto de design "Renovação
// frontend detalhes pedido" (1a Laudo · 1b Console · 1c Terminal · 2a
// Reconciliação · 2b Cockpit · 3a Extrato de conciliação):
//   - coluna única (1a/2a/3a, direção mais recente), absorvendo do rail
//     (1b/2b) a saúde com regra-id e o take-rate como faixa compacta;
//   - veredito no topo respondendo UMA pergunta (3a);
//   - a ponte de âncoras como hero (3a) — AQUI estendida a 3 vias com o
//     SIMULADO (contrato ƒ da fee_reference), que não existia nos designs;
//   - o dinheiro UMA vez só, como ledger (2a/3a) — com a coluna Simulado;
//   - caixa "fora da conciliação" anti-contagem-dupla (2a/2b/3a);
//   - extrato como prova (3a/1c), timeline em linhas com pill de fonte (2a),
//     apoio recolhido (3a), evidência raw apontando pra página atual.
// Paleta fixa clara (é uma experiência; não segue o dark-mode do app).
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../services/api';
import { FormatMoney } from '../utils/format';

// `money` da experiência = FormatMoney compartilhado (guard '—' idêntico).
// `dateTime`/`pct` ficam locais de propósito: formatos exclusivos do design
// desta página (ano 2 dígitos / percentual 2 casas com vírgula).
const money = (v) => FormatMoney(v);
const signed = (v) => (v == null ? '—' : `${v < 0 ? '− ' : '+ '}${money(Math.abs(v))}`);
const dateTime = (v) => (v ? new Date(v).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '—');
const pct = (v) => (v == null ? '—' : `${(v * 100).toFixed(2).replace('.', ',')}%`);

// paleta da experiência (fixa, tema papel — meio comum dos designs)
const C = {
    paper: '#f7f6f2',
    card: '#ffffff',
    ink: '#23262e',
    sub: '#6b6f79',
    faint: '#a49d8c',
    border: '#e6e3da',
    ml: '#e8a33d',
    mp: '#3483fa',
    ext: '#14b8a6',
    nfe: '#8b5cf6',
    ok: '#15803d',
    okBg: '#e2f2e6',
    warn: '#8a5c0a',
    warnBg: '#fdf0d5',
    crit: '#b42318',
    critBg: '#fdecea',
    dark: '#1a1f28',
};

const VERDICT_STYLE = {
    concilia: { dot: '#22a55a', color: C.ok, bg: C.okBg },
    ressalva: { dot: '#e0a218', color: C.warn, bg: C.warnBg },
    nao_concilia: { dot: '#dc2626', color: C.crit, bg: C.critBg },
    aguardando: { dot: '#9aa0ab', color: '#5a5e68', bg: '#eef0f3' },
    dentro: { dot: '#22a55a', color: C.ok, bg: C.okBg },
    fora: { dot: '#dc2626', color: C.crit, bg: C.critBg },
    indisponivel: { dot: '#9aa0ab', color: '#5a5e68', bg: '#eef0f3' },
};
const HEALTH_STYLE = {
    ok: { dot: '#22a55a', label: 'OK', color: '#3a3f49' },
    pending: { dot: '#c3c7cf', label: 'aguardando', color: '#8b8f99' },
    warn: { dot: '#e0a218', label: 'atenção', color: C.warn },
    critical: { dot: '#dc2626', label: 'crítico', color: C.crit },
};
const DIM_LABEL = {
    COMERCIAL: 'Venda',
    PAGAMENTO: 'Pagamento',
    LIQUIDACAO: 'Recebimento',
    FISCAL: 'Fiscal',
    LOGISTICA: 'Logística',
};
const SRC_PILL = {
    ml: { bg: '#f7edd8', color: '#8a5c0a', label: 'ML' },
    mp: { bg: '#e3edfb', color: '#1d4ed8', label: 'MP' },
    ext: { bg: '#daf1ed', color: '#0f766e', label: 'EXTRATO' },
    nfe: { bg: '#ece5fb', color: '#6d28d9', label: 'NF-e' },
};

const label10 = {
    font: '600 10px ui-sans-serif, system-ui',
    letterSpacing: '.09em',
    textTransform: 'uppercase',
    color: C.faint,
};
const monoNum = { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontVariantNumeric: 'tabular-nums' };

function Dot({ color, size = 8, radius = 2 }) {
    return (
        <span
            style={{
                display: 'inline-block',
                width: size,
                height: size,
                borderRadius: radius,
                background: color,
                flex: 'none',
            }}
        />
    );
}

// ── âncora da ponte (Simulado ƒ / Previsto / Realizado) ─────────────────────
function Anchor({ kind, title, value, sub, empty }) {
    const styles = {
        sim: {
            background: '#fbfaf6',
            border: `1.5px dashed ${empty ? '#d9d4c8' : '#b9b2a2'}`,
            color: C.ink,
            titleColor: '#7a7466',
            radius: '10px 0 0 10px',
        },
        prev: {
            background: C.card,
            border: `1px solid #e2e6ec`,
            borderLeft: `3px solid ${C.mp}`,
            color: C.ink,
            titleColor: '#3568c4',
            radius: '0',
        },
        real: {
            background: empty ? '#f0f1f3' : C.dark,
            border: '1px solid transparent',
            color: empty ? '#8b8f99' : '#fff',
            titleColor: empty ? '#9aa0ab' : '#5ecdbb',
            radius: '0 10px 10px 0',
        },
    }[kind];
    return (
        <div
            style={{
                padding: '16px 20px',
                display: 'flex',
                flexDirection: 'column',
                gap: 5,
                borderRadius: styles.radius,
                background: styles.background,
                border: styles.border,
                borderLeft: styles.borderLeft || styles.border,
                color: styles.color,
            }}
        >
            <span style={{ ...label10, color: styles.titleColor }}>{title}</span>
            <span style={{ ...monoNum, fontSize: 26, fontWeight: 600, letterSpacing: '-.02em' }}>{value}</span>
            <span style={{ fontSize: 11, color: kind === 'real' && !empty ? '#98a1af' : '#8b8f99' }}>{sub}</span>
        </div>
    );
}

function DeltaBridge({ label, delta, reason, good }) {
    const color = delta == null ? '#9aa0ab' : Math.abs(delta) <= 0.05 ? '#22a55a' : good ? C.ok : C.crit;
    return (
        <div
            style={{
                padding: '0 14px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 5,
            }}
        >
            <span style={{ ...label10, color: C.faint }}>{label}</span>
            <span style={{ ...monoNum, fontSize: 17, fontWeight: 600, color }}>
                {delta == null ? '—' : Math.abs(delta) <= 0.05 ? '= 0,00' : signed(delta)}
            </span>
            <span style={{ fontSize: 14, color: '#c3c0b6' }}>→</span>
            {reason ? (
                <span
                    style={{
                        padding: '2px 8px',
                        borderRadius: 999,
                        background: good ? C.okBg : C.warnBg,
                        color: good ? C.ok : C.warn,
                        fontSize: 9.5,
                        fontWeight: 600,
                        textAlign: 'center',
                        maxWidth: 110,
                    }}
                >
                    {reason}
                </span>
            ) : null}
        </div>
    );
}

export default function PaymentOrderExperience() {
    const { orderId } = useParams();
    const navigate = useNavigate();
    const [exp, setExp] = useState(null);
    const [movements, setMovements] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [openMemoria, setOpenMemoria] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [e, movs] = await Promise.all([
                api.getOrderExperience(orderId),
                api.getOrderMovements(orderId).catch(() => []),
            ]);
            setExp(e);
            setMovements(Array.isArray(movs) ? movs : []);
        } catch (err) {
            setError(err.message || 'Erro ao carregar');
        } finally {
            setLoading(false);
        }
    }, [orderId]);
    useEffect(() => {
        load();
    }, [load]);

    if (loading)
        return (
            <div
                style={{
                    background: C.paper,
                    minHeight: '60vh',
                    borderRadius: 12,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: C.sub,
                    fontSize: 13,
                }}
            >
                Montando a conciliação…
            </div>
        );
    if (error || !exp)
        return (
            <div style={{ background: C.paper, borderRadius: 12, padding: 40, color: C.crit, fontSize: 13 }}>
                {error || 'Pedido não encontrado.'}
            </div>
        );

    const a = exp.analysis || {};
    const { verdicts, totals, simulado, previsto, realizado, ledger } = exp;
    const vLiq = VERDICT_STYLE[verdicts.liquidacao.state] || VERDICT_STYLE.aguardando;
    const vCon = VERDICT_STYLE[verdicts.contrato.state] || VERDICT_STYLE.indisponivel;
    const orderStatus = a?.kpis?.venda?.cancelled ? 'CANCELADA' : 'PAGO';
    const alerts = (a.rules || []).filter((r) => ['critical', 'warn'].includes(r.severity));
    const infos = (a.rules || []).filter((r) => r.severity === 'info');
    const takeEfetivo = previsto.venda > 0 ? (previsto.venda - previsto.liquido) / previsto.venda : null;
    const takeContrato =
        simulado.available && simulado.venda > 0 ? (simulado.venda - simulado.liquido) / simulado.venda : null;
    const nf = a?.kpis?.nf || {};

    const rubStatusCell = (l) => {
        if (l.key === 'venda') return <span style={{ color: '#22a55a', fontSize: 13 }}>✓</span>;
        if (l.status === 'acima_contrato')
            return <span style={{ fontSize: 10.5, fontWeight: 600, color: C.crit }}>acima</span>;
        if (l.status === 'abaixo_contrato')
            return <span style={{ fontSize: 10.5, fontWeight: 600, color: C.ok }}>abaixo</span>;
        if (l.sim == null) return <span style={{ color: '#c3c0b6' }}>·</span>;
        return <span style={{ color: '#22a55a', fontSize: 13 }}>✓</span>;
    };

    return (
        <div
            style={{
                background: C.paper,
                color: C.ink,
                borderRadius: 12,
                border: `1px solid ${C.border}`,
                overflow: 'hidden',
                fontFamily: 'ui-sans-serif, system-ui',
            }}
        >
            {/* ── header ── */}
            <div
                style={{
                    padding: '20px 32px 16px',
                    borderBottom: `1px solid ${C.border}`,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 12,
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 12, color: '#8b8f99' }}>Conciliação /</span>
                    <span style={{ ...monoNum, fontSize: 17, fontWeight: 600 }}>{orderId}</span>
                    <span
                        style={{
                            padding: '3px 10px',
                            borderRadius: 4,
                            background: orderStatus === 'PAGO' ? C.okBg : C.warnBg,
                            color: orderStatus === 'PAGO' ? C.ok : C.warn,
                            fontSize: 10.5,
                            fontWeight: 600,
                            letterSpacing: '.05em',
                        }}
                    >
                        {orderStatus}
                    </span>
                    <span
                        style={{
                            padding: '3px 10px',
                            borderRadius: 4,
                            background: '#efe9fc',
                            color: '#6d28d9',
                            fontSize: 10,
                            fontWeight: 600,
                            letterSpacing: '.05em',
                        }}
                        title="página experimental — a atual continua em /payments/order/:id"
                    >
                        EXPERIÊNCIA
                    </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 11, fontSize: 10.5, color: '#8b8f99' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                            <Dot color={C.ml} />
                            ML
                        </span>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                            <Dot color={C.mp} />
                            MP · previsto
                        </span>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                            <Dot color={C.ext} />
                            Extrato · realizado
                        </span>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                            <Dot color={C.nfe} />
                            NF-e
                        </span>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontStyle: 'italic' }}>
                            ƒ Simulado · contrato
                        </span>
                    </div>
                    <button
                        type="button"
                        onClick={() => navigate(`/payments/order/${orderId}`)}
                        style={{
                            padding: '7px 13px',
                            border: `1px solid #d9d4c8`,
                            borderRadius: 6,
                            background: '#fff',
                            fontSize: 11.5,
                            fontWeight: 600,
                            color: '#5a5e68',
                            cursor: 'pointer',
                        }}
                    >
                        Página atual
                    </button>
                    <button
                        type="button"
                        onClick={load}
                        style={{
                            padding: '7px 13px',
                            border: 'none',
                            borderRadius: 6,
                            background: C.dark,
                            color: '#fff',
                            fontSize: 11.5,
                            fontWeight: 600,
                            cursor: 'pointer',
                        }}
                    >
                        Atualizar
                    </button>
                </div>
            </div>

            {/* ── veredito + ponte de 3 âncoras ── */}
            <div
                style={{
                    padding: '22px 32px',
                    borderBottom: `1px solid ${C.border}`,
                    background: 'linear-gradient(180deg,#fffdf7,#f7f6f2)',
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
                    <span style={{ width: 11, height: 11, borderRadius: '50%', background: vLiq.dot, flex: 'none' }} />
                    <span style={{ fontSize: 15, fontWeight: 700, color: vLiq.color }}>
                        {verdicts.liquidacao.label}
                    </span>
                    <span style={{ fontSize: 12.5, color: C.sub }}>— {verdicts.liquidacao.detail}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18, flexWrap: 'wrap' }}>
                    <span style={{ width: 11, height: 11, borderRadius: '50%', background: vCon.dot, flex: 'none' }} />
                    <span style={{ fontSize: 13.5, fontWeight: 700, color: vCon.color }}>
                        {verdicts.contrato.label}
                    </span>
                    <span style={{ fontSize: 12, color: C.sub }}>— {verdicts.contrato.detail}</span>
                    {verdicts.contrato.weakRef ? (
                        <span
                            style={{
                                fontSize: 10.5,
                                color: C.warn,
                                background: C.warnBg,
                                padding: '1px 7px',
                                borderRadius: 999,
                            }}
                        >
                            referência fraca
                        </span>
                    ) : null}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr auto 1fr', alignItems: 'stretch' }}>
                    <Anchor
                        kind="sim"
                        title="Simulado · contrato ƒ"
                        value={simulado.available ? money(simulado.liquido) : '—'}
                        sub={
                            simulado.available
                                ? `memória de cálculo do anúncio${simulado.weakRef ? ' (fallback)' : ''}`
                                : simulado.reason
                        }
                        empty={!simulado.available}
                    />
                    <DeltaBridge
                        label="Δ contrato"
                        delta={totals.deltaContrato}
                        good={verdicts.contrato.state !== 'fora'}
                        reason={
                            totals.deltaContrato != null && Math.abs(totals.deltaContrato) > 0.05
                                ? verdicts.contrato.state === 'fora'
                                    ? 'acima do contrato'
                                    : totals.deltaContrato > 0
                                      ? 'melhor que o contrato'
                                      : 'parcelamento / meio de pagto'
                                : null
                        }
                    />
                    <Anchor
                        kind="prev"
                        title="Previsto · pagamento MP"
                        value={money(previsto.liquido)}
                        sub="net_received_amount no ato da venda"
                    />
                    <DeltaBridge
                        label="Δ liquidação"
                        delta={totals.deltaLiq}
                        good={totals.deltaLiq != null && totals.deltaLiq >= 0}
                        reason={verdicts.liquidacao.state === 'ressalva' ? 'fee_discount campanha' : null}
                    />
                    <Anchor
                        kind="real"
                        title="Realizado · extrato"
                        value={realizado.available ? money(realizado.liquido) : '—'}
                        sub={
                            realizado.available
                                ? `${realizado.lines} lançamento(s) de liberação`
                                : 'aguardando o extrato cobrir a liberação'
                        }
                        empty={!realizado.available}
                    />
                </div>

                <div
                    style={{
                        marginTop: 12,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 16,
                        fontSize: 11.5,
                        color: C.sub,
                        flexWrap: 'wrap',
                    }}
                >
                    <span>
                        Comprador pagou{' '}
                        <span style={{ ...monoNum, fontWeight: 500, color: '#4a4e58' }}>
                            {money(previsto.compradorPagou)}
                        </span>
                    </span>
                    <span style={{ color: '#d3cfc4' }}>·</span>
                    <span>
                        Venda{' '}
                        <span style={{ ...monoNum, fontWeight: 500, color: '#4a4e58' }}>{money(previsto.venda)}</span>
                    </span>
                    <span style={{ color: '#d3cfc4' }}>·</span>
                    <span>
                        Take rate efetivo{' '}
                        <span style={{ ...monoNum, fontWeight: 500, color: '#4a4e58' }}>{pct(takeEfetivo)}</span>
                        {takeContrato != null ? (
                            <span style={{ color: C.faint }}> vs contrato {pct(takeContrato)}</span>
                        ) : null}
                    </span>
                </div>
            </div>

            {/* ── saúde (rail 1b/2b absorvido como faixa 2a) ── */}
            {a.health ? (
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 18,
                        padding: '10px 32px',
                        borderBottom: `1px solid ${C.border}`,
                        background: '#f6f5f0',
                        flexWrap: 'wrap',
                    }}
                >
                    <span style={label10}>Saúde</span>
                    {Object.entries(a.health.dimensions || {}).map(([dim, h]) => {
                        const hs = HEALTH_STYLE[h.state] || HEALTH_STYLE.pending;
                        return (
                            <span
                                key={dim}
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 6,
                                    fontSize: 11.5,
                                    fontWeight: 600,
                                    color: hs.color,
                                }}
                                title={h.rules?.length ? `regras: ${h.rules.join(', ')}` : hs.label}
                            >
                                <span style={{ width: 8, height: 8, borderRadius: '50%', background: hs.dot }} />
                                {DIM_LABEL[dim] || dim}
                                {h.rules?.length ? (
                                    <span style={{ ...monoNum, fontSize: 9.5, color: C.faint }}>
                                        {h.rules.join(' ')}
                                    </span>
                                ) : null}
                            </span>
                        );
                    })}
                    <span style={{ marginLeft: 'auto', fontSize: 11, color: C.faint }}>
                        {alerts.length ? `${alerts.length} alerta(s)` : 'sem alertas críticos'}
                    </span>
                </div>
            ) : null}

            {/* ── alertas ── */}
            {alerts.length > 0 ? (
                <div style={{ padding: '18px 32px', borderBottom: `1px solid ${C.border}` }}>
                    <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10 }}>
                        Precisa de atenção{' '}
                        <span
                            style={{
                                padding: '1px 7px',
                                borderRadius: 999,
                                background: '#e9e6dd',
                                fontSize: 10.5,
                                fontWeight: 600,
                                color: '#6b6558',
                            }}
                        >
                            {alerts.length}
                        </span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {alerts.map((r) => (
                            <div
                                key={r.id}
                                style={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: 12,
                                    padding: '12px 16px',
                                    background: r.severity === 'critical' ? C.critBg : '#fffdf7',
                                    border: `1px solid ${r.severity === 'critical' ? '#f3c1bb' : '#f0dcae'}`,
                                    borderLeft: `3px solid ${r.severity === 'critical' ? '#dc2626' : '#e0a218'}`,
                                    borderRadius: 8,
                                }}
                            >
                                <span
                                    style={{
                                        flex: 'none',
                                        marginTop: 1,
                                        padding: '2px 8px',
                                        borderRadius: 4,
                                        background: r.severity === 'critical' ? '#f6d1cc' : '#f7e6c2',
                                        color: r.severity === 'critical' ? C.crit : C.warn,
                                        fontSize: 10,
                                        fontWeight: 600,
                                        letterSpacing: '.06em',
                                    }}
                                >
                                    {r.severity === 'critical' ? 'CRÍTICO' : 'ATENÇÃO'}
                                </span>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                        <span style={{ fontSize: 13, fontWeight: 600 }}>{r.title}</span>
                                        <span
                                            style={{
                                                ...monoNum,
                                                fontSize: 10.5,
                                                fontWeight: 600,
                                                color: C.faint,
                                                border: `1px solid #ddd8cb`,
                                                borderRadius: 4,
                                                padding: '1px 5px',
                                            }}
                                        >
                                            {r.id}
                                        </span>
                                    </div>
                                    <div style={{ fontSize: 12, color: C.sub, marginTop: 3, lineHeight: 1.5 }}>
                                        {r.message}
                                    </div>
                                    {r.action ? (
                                        <div style={{ fontSize: 12, marginTop: 3 }}>
                                            <strong>O que fazer:</strong> {r.action}
                                        </div>
                                    ) : null}
                                </div>
                            </div>
                        ))}
                    </div>
                    {infos.length ? (
                        <div style={{ marginTop: 8, fontSize: 11.5, color: C.sub }}>
                            {infos.map((r) => (
                                <span key={r.id} style={{ marginRight: 14 }}>
                                    ℹ {r.title} <span style={{ ...monoNum, fontSize: 10, color: C.faint }}>{r.id}</span>
                                </span>
                            ))}
                        </div>
                    ) : null}
                </div>
            ) : null}

            {/* ── ledger de conciliação em 3 vias ── */}
            <div style={{ padding: '20px 32px', borderBottom: `1px solid ${C.border}` }}>
                <div style={{ fontSize: 14, fontWeight: 700 }}>A conciliação, rubrica a rubrica</div>
                <p style={{ margin: '3px 0 14px', fontSize: 12, color: '#8b8f99' }}>
                    <span style={{ fontStyle: 'italic' }}>ƒ Simulado</span> = a memória de cálculo do contrato ·{' '}
                    <span style={{ color: '#3568c4' }}>Previsto</span> = o que o MP cobrou no ato ·{' '}
                    <span style={{ color: '#0f766e' }}>Realizado</span> = o que caiu na conta.
                </p>
                <div style={{ overflowX: 'auto' }}>
                    <div style={{ minWidth: 860 }}>
                        <div
                            style={{
                                display: 'grid',
                                gridTemplateColumns: '1fr 120px 120px 120px 110px 76px',
                                ...label10,
                                paddingBottom: 8,
                                borderBottom: `1px solid ${C.border}`,
                            }}
                        >
                            <span>Rubrica</span>
                            <span style={{ textAlign: 'right' }}>Simulado ƒ</span>
                            <span style={{ textAlign: 'right' }}>Previsto · MP</span>
                            <span style={{ textAlign: 'right' }}>Realizado</span>
                            <span style={{ textAlign: 'right' }}>Δ contrato</span>
                            <span style={{ textAlign: 'right' }}>Status</span>
                        </div>
                        {ledger.map((l) => {
                            const divergent = l.status === 'acima_contrato' || l.status === 'abaixo_contrato';
                            const fmt = (v) =>
                                v == null
                                    ? l.simNote && v === l.sim
                                        ? l.simNote
                                        : '—'
                                    : l.cost
                                      ? `− ${money(v).replace('R$ ', '')}`
                                      : money(v).replace('R$ ', '');
                            return (
                                <div
                                    key={l.key}
                                    style={{
                                        display: 'grid',
                                        gridTemplateColumns: '1fr 120px 120px 120px 110px 76px',
                                        alignItems: 'baseline',
                                        padding: '9px 0',
                                        borderBottom: '1px solid #eeece4',
                                        fontSize: 13,
                                        background: divergent ? '#fffcf4' : 'transparent',
                                    }}
                                >
                                    <span>
                                        {l.label}{' '}
                                        <span style={{ ...monoNum, fontSize: 10.5, color: C.faint }}>{l.field}</span>
                                        {l.simNote ? (
                                            <span style={{ fontSize: 10, color: C.faint }}> · {l.simNote}</span>
                                        ) : null}
                                    </span>
                                    <span
                                        style={{
                                            ...monoNum,
                                            textAlign: 'right',
                                            fontWeight: 500,
                                            color: l.sim == null ? '#c3c0b6' : '#7a7466',
                                            fontStyle: 'italic',
                                        }}
                                    >
                                        {l.sim == null ? '—' : fmt(l.sim)}
                                    </span>
                                    <span style={{ ...monoNum, textAlign: 'right', fontWeight: 500, color: '#3568c4' }}>
                                        {fmt(l.prev)}
                                    </span>
                                    <span
                                        style={{
                                            ...monoNum,
                                            textAlign: 'right',
                                            fontWeight: 500,
                                            color: l.real == null ? '#c3c0b6' : '#0f766e',
                                        }}
                                    >
                                        {l.real == null ? '—' : fmt(l.real)}
                                    </span>
                                    <span
                                        style={{
                                            ...monoNum,
                                            textAlign: 'right',
                                            fontWeight: 600,
                                            fontSize: 12,
                                            color:
                                                l.deltaContrato == null
                                                    ? '#c3c0b6'
                                                    : Math.abs(l.deltaContrato) <= 0.05
                                                      ? '#c3c0b6'
                                                      : l.deltaContrato > 0
                                                        ? C.ok
                                                        : C.crit,
                                        }}
                                    >
                                        {l.deltaContrato == null || Math.abs(l.deltaContrato) <= 0.05
                                            ? '—'
                                            : signed(l.deltaContrato)}
                                    </span>
                                    <span style={{ textAlign: 'right' }}>{rubStatusCell(l)}</span>
                                </div>
                            );
                        })}
                        <div
                            style={{
                                display: 'grid',
                                gridTemplateColumns: '1fr 120px 120px 120px 110px 76px',
                                alignItems: 'baseline',
                                padding: '11px 0',
                                borderTop: `2px solid ${C.ink}`,
                                fontSize: 14,
                                fontWeight: 700,
                            }}
                        >
                            <span>= Líquido do pedido</span>
                            <span
                                style={{
                                    ...monoNum,
                                    textAlign: 'right',
                                    fontStyle: 'italic',
                                    color: totals.sim == null ? '#c3c0b6' : '#7a7466',
                                }}
                            >
                                {totals.sim == null ? '—' : money(totals.sim).replace('R$ ', '')}
                            </span>
                            <span style={{ ...monoNum, textAlign: 'right', color: '#3568c4' }}>
                                {money(totals.prev).replace('R$ ', '')}
                            </span>
                            <span
                                style={{
                                    ...monoNum,
                                    textAlign: 'right',
                                    color: totals.real == null ? '#c3c0b6' : '#0f766e',
                                }}
                            >
                                {totals.real == null ? '—' : money(totals.real).replace('R$ ', '')}
                            </span>
                            <span
                                style={{
                                    ...monoNum,
                                    textAlign: 'right',
                                    fontSize: 13,
                                    color:
                                        totals.deltaContrato == null
                                            ? '#c3c0b6'
                                            : totals.deltaContrato >= 0
                                              ? C.ok
                                              : C.crit,
                                }}
                            >
                                {totals.deltaContrato == null ? '—' : signed(totals.deltaContrato)}
                            </span>
                            <span />
                        </div>
                    </div>
                </div>

                {/* memória de cálculo */}
                {simulado.available ? (
                    <div
                        style={{ marginTop: 14, border: `1px dashed #d9d4c8`, borderRadius: 8, background: '#fbfaf6' }}
                    >
                        <button
                            type="button"
                            onClick={() => setOpenMemoria((v) => !v)}
                            style={{
                                width: '100%',
                                textAlign: 'left',
                                padding: '11px 16px',
                                background: 'transparent',
                                border: 'none',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                            }}
                        >
                            <span style={{ color: '#a3a7af', fontSize: 11 }}>{openMemoria ? '▾' : '▸'}</span>
                            <span style={{ fontSize: 12, fontWeight: 700, fontStyle: 'italic' }}>
                                ƒ Memória de cálculo do Simulado
                            </span>
                            <span style={{ fontSize: 11, color: '#8b8f99' }}>
                                como chegamos em {money(simulado.liquido)}
                            </span>
                        </button>
                        {openMemoria ? (
                            <div
                                style={{
                                    padding: '0 16px 14px',
                                    fontSize: 12,
                                    color: C.sub,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 8,
                                }}
                            >
                                <div
                                    style={{
                                        ...monoNum,
                                        fontSize: 11.5,
                                        background: '#f2f0e9',
                                        padding: '8px 12px',
                                        borderRadius: 6,
                                    }}
                                >
                                    {simulado.memoria.formula}
                                </div>
                                {simulado.items.map((i) => (
                                    <div
                                        key={i.mlb}
                                        style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}
                                    >
                                        <span style={{ ...monoNum, fontWeight: 600, color: C.ink }}>{i.mlb}</span>
                                        {i.sku ? (
                                            <span style={{ ...monoNum, fontSize: 11, color: C.faint }}>{i.sku}</span>
                                        ) : null}
                                        {i.ref ? (
                                            <span>
                                                comissão <strong>{pct(i.ref.comissaoPct)}</strong> × {money(i.base)} ={' '}
                                                <strong>{money(i.comissaoSim)}</strong>
                                                <span style={{ color: C.faint }}>
                                                    {' '}
                                                    · ref {i.ref.scope} · {i.ref.sampleN.toLocaleString('pt-BR')} vendas
                                                    · IQR ±{pct(i.ref.comissaoIqrPct / 2)}
                                                </span>
                                            </span>
                                        ) : (
                                            <span style={{ color: C.warn }}>
                                                sem referência — item fora do simulado
                                            </span>
                                        )}
                                    </div>
                                ))}
                                <div>
                                    frete do envio (mediana do anúncio dominante){' '}
                                    <strong>{money(simulado.frete)}</strong> · processamento{' '}
                                    <strong>{money(simulado.proc)}</strong>{' '}
                                    <span style={{ color: C.faint }}>(~1% — varia por meio de pagamento)</span> ·
                                    parcelamento fora do contrato-base
                                </div>
                                <div style={{ fontSize: 11, color: C.faint }}>{simulado.memoria.fonte}</div>
                            </div>
                        ) : null}
                    </div>
                ) : null}

                {/* fora da conciliação */}
                {previsto.foraDaConciliacao.length > 0 ? (
                    <div
                        style={{
                            marginTop: 12,
                            border: `1px dashed #d9d4c8`,
                            borderRadius: 8,
                            padding: '12px 16px',
                            background: '#faf9f4',
                        }}
                    >
                        <div style={{ ...label10, marginBottom: 8 }}>
                            Fora da conciliação — não é dinheiro do vendedor (evita contagem dupla)
                        </div>
                        <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
                            {previsto.foraDaConciliacao.map((f) => (
                                <span key={f.label + f.field} style={{ fontSize: 11.5, color: C.sub }} title={f.note}>
                                    {f.label}{' '}
                                    <span style={{ ...monoNum, fontSize: 10.5, color: C.faint }}>{f.field}</span>{' '}
                                    <span style={{ ...monoNum, fontWeight: 500, color: C.faint }}>
                                        {signed(f.value)} ✗
                                    </span>
                                </span>
                            ))}
                        </div>
                    </div>
                ) : null}
            </div>

            {/* ── o que caiu na conta (prova) ── */}
            <div style={{ padding: '20px 32px', borderBottom: `1px solid ${C.border}` }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 13, fontWeight: 700 }}>O que caiu na conta</span>
                    <span
                        style={{
                            padding: '2px 8px',
                            borderRadius: 4,
                            background: '#daf1ed',
                            color: '#0f766e',
                            fontSize: 10,
                            fontWeight: 600,
                            letterSpacing: '.05em',
                        }}
                    >
                        EXTRATO · RELEASED MONEY
                    </span>
                    <span style={{ fontSize: 11.5, color: '#8b8f99' }}>
                        {movements.length
                            ? `${movements.length} lançamento(s) desta order na janela importada`
                            : 'nenhum lançamento na janela do extrato importado'}
                    </span>
                </div>
                {movements.length ? (
                    <div style={{ overflowX: 'auto' }}>
                        <div style={{ minWidth: 760 }}>
                            <div
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns: '140px 1.4fr 90px 90px 110px 110px',
                                    ...label10,
                                    paddingBottom: 7,
                                    borderBottom: `1px solid ${C.border}`,
                                }}
                            >
                                <span>Data</span>
                                <span>Descrição</span>
                                <span style={{ textAlign: 'right' }}>MP fee</span>
                                <span style={{ textAlign: 'right' }}>Frete</span>
                                <span style={{ textAlign: 'right' }}>Crédito</span>
                                <span style={{ textAlign: 'right' }}>Débito</span>
                            </div>
                            {movements.map((m) => {
                                const internal =
                                    /^reserve_/.test(String(m.description || '')) ||
                                    /payout/.test(String(m.description || ''));
                                return (
                                    <div
                                        key={m.id}
                                        style={{
                                            display: 'grid',
                                            gridTemplateColumns: '140px 1.4fr 90px 90px 110px 110px',
                                            alignItems: 'baseline',
                                            padding: '8px 0',
                                            borderBottom: '1px solid #eeece4',
                                            opacity: internal ? 0.55 : 1,
                                        }}
                                    >
                                        <span style={{ ...monoNum, fontSize: 11, color: C.sub }}>
                                            {dateTime(m.movement_date)}
                                        </span>
                                        <span style={{ ...monoNum, fontSize: 12, fontWeight: 500 }}>
                                            {m.description}
                                            {internal ? (
                                                <span style={{ fontSize: 10, color: C.faint }}>
                                                    {' '}
                                                    · interno (par que zera)
                                                </span>
                                            ) : null}
                                        </span>
                                        <span style={{ ...monoNum, textAlign: 'right', fontSize: 12, color: C.sub }}>
                                            {Number(m.mp_fee_amount) ? money(m.mp_fee_amount).replace('R$ ', '') : ''}
                                        </span>
                                        <span style={{ ...monoNum, textAlign: 'right', fontSize: 12, color: C.sub }}>
                                            {Number(m.shipping_fee_amount)
                                                ? money(m.shipping_fee_amount).replace('R$ ', '')
                                                : ''}
                                        </span>
                                        <span style={{ ...monoNum, textAlign: 'right', fontSize: 12, color: C.ok }}>
                                            {Number(m.net_credit_amount) > 0
                                                ? `+ ${money(m.net_credit_amount).replace('R$ ', '')}`
                                                : ''}
                                        </span>
                                        <span style={{ ...monoNum, textAlign: 'right', fontSize: 12, color: C.crit }}>
                                            {Number(m.net_debit_amount) > 0
                                                ? `− ${money(m.net_debit_amount).replace('R$ ', '')}`
                                                : ''}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ) : (
                    <div
                        style={{
                            padding: '14px 16px',
                            borderRadius: 8,
                            background: '#eef0f3',
                            fontSize: 12,
                            color: '#5a5e68',
                        }}
                    >
                        Aguardando o extrato cobrir a data de liberação deste pedido — a coluna "Realizado" preenche
                        sozinha quando o relatório diário chegar.
                    </div>
                )}
            </div>

            {/* ── apoio recolhido ── */}
            <div style={{ padding: '18px 32px 24px' }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 10 }}>
                    <span style={{ fontSize: 13, fontWeight: 700 }}>Apoio</span>
                    <span style={{ fontSize: 11.5, color: '#8b8f99' }}>
                        não responde "o dinheiro bateu?" — fica recolhido
                    </span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 8 }}>
                    <details style={{ border: `1px solid ${C.border}`, borderRadius: 6, background: '#fff' }}>
                        <summary
                            style={{
                                cursor: 'pointer',
                                padding: '10px 14px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 10,
                                fontSize: 12.5,
                                fontWeight: 500,
                                listStyle: 'none',
                            }}
                        >
                            <Dot color={C.nfe} /> Fiscal · NF-e
                            <span style={{ fontSize: 11, color: '#8b8f99' }}>
                                {nf.status === 'AUTORIZADA'
                                    ? `${nf.numero}/${nf.serie} autorizada`
                                    : nf.status === 'CANCELADA'
                                      ? `${nf.numero}/${nf.serie} cancelada`
                                      : (nf.status || '—').toLowerCase()}
                                {a?.fiscal?.valueCheck
                                    ? a.fiscal.valueCheck.status === 'ok'
                                        ? ' · vNF = venda ✓'
                                        : ` · Δ ${money(a.fiscal.valueCheck.delta)}`
                                    : ''}
                            </span>
                        </summary>
                        <div
                            style={{
                                padding: '0 14px 12px',
                                fontSize: 12,
                                color: C.sub,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 5,
                            }}
                        >
                            {(a?.fiscal?.vendas || []).concat(a?.fiscal?.devolucoes || []).map((inv) => (
                                <div key={inv.chave} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <span>
                                        <strong>
                                            {inv.kind === 'DEVOLUCAO' ? 'NF devolução' : 'NF-e'} {inv.numero}/
                                            {inv.serie}
                                        </strong>{' '}
                                        · vNF {money(inv.valorTotal)} ·{' '}
                                        {inv.statusCodigo === '100' && !inv.hasCancelEvent ? 'autorizada' : 'cancelada'}
                                    </span>
                                    <span
                                        style={{ ...monoNum, fontSize: 10.5, color: C.faint, wordBreak: 'break-all' }}
                                    >
                                        {inv.chave}
                                    </span>
                                </div>
                            ))}
                            <span style={{ fontSize: 11, color: C.faint }}>
                                XML e impostos por item: na{' '}
                                <button
                                    type="button"
                                    onClick={() => navigate(`/payments/order/${orderId}`)}
                                    style={{
                                        border: 'none',
                                        background: 'none',
                                        color: '#2563eb',
                                        cursor: 'pointer',
                                        padding: 0,
                                        fontSize: 11,
                                    }}
                                >
                                    página atual
                                </button>
                            </span>
                        </div>
                    </details>
                    <details style={{ border: `1px solid ${C.border}`, borderRadius: 6, background: '#fff' }}>
                        <summary
                            style={{
                                cursor: 'pointer',
                                padding: '10px 14px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 10,
                                fontSize: 12.5,
                                fontWeight: 500,
                                listStyle: 'none',
                            }}
                        >
                            <Dot color="#c3c7cf" /> Linha do tempo
                            <span style={{ fontSize: 11, color: '#8b8f99' }}>
                                {(a.timeline || []).length} evento(s) das 4 fontes
                            </span>
                        </summary>
                        <div style={{ padding: '0 14px 12px' }}>
                            {(a.timeline || []).map((e) => {
                                const pill = SRC_PILL[e.source] || { bg: '#eef0f3', color: '#6b7280', label: e.source };
                                return (
                                    <div
                                        key={`${e.ts}-${e.source}-${e.label}`}
                                        style={{
                                            display: 'grid',
                                            gridTemplateColumns: '110px 74px 1fr auto',
                                            alignItems: 'baseline',
                                            padding: '6px 0',
                                            borderTop: '1px solid #eeece4',
                                            fontSize: 12,
                                        }}
                                    >
                                        <span style={{ ...monoNum, fontSize: 10.5, color: '#8b8f99' }}>
                                            {dateTime(e.ts)}
                                        </span>
                                        <span>
                                            <span
                                                style={{
                                                    padding: '2px 7px',
                                                    borderRadius: 4,
                                                    background: pill.bg,
                                                    color: pill.color,
                                                    fontSize: 9.5,
                                                    fontWeight: 600,
                                                }}
                                            >
                                                {pill.label}
                                            </span>
                                        </span>
                                        <span style={{ fontWeight: 500, color: e.kind === 'problem' ? C.crit : C.ink }}>
                                            {e.label}
                                            {e.kind === 'forecast' ? ' (previsto)' : ''}
                                        </span>
                                        <span
                                            style={{
                                                ...monoNum,
                                                fontSize: 11.5,
                                                color: e.amount > 0 ? C.ok : e.amount < 0 ? C.crit : C.faint,
                                            }}
                                        >
                                            {e.amount ? signed(e.amount) : ''}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    </details>
                    <div
                        style={{
                            border: `1px solid ${C.border}`,
                            borderRadius: 6,
                            background: '#fff',
                            padding: '10px 14px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 10,
                            fontSize: 12.5,
                            fontWeight: 500,
                        }}
                    >
                        <Dot color="#c3c7cf" /> Logística
                        <span style={{ fontSize: 11, color: '#8b8f99' }}>
                            só o ID do envio está no banco — sem rastreio/custo por etiqueta
                        </span>
                    </div>
                    <div
                        style={{
                            border: `1px solid ${C.border}`,
                            borderRadius: 6,
                            background: '#fff',
                            padding: '10px 14px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 10,
                            fontSize: 12.5,
                            fontWeight: 500,
                        }}
                    >
                        <span style={{ color: '#a3a7af', fontSize: 11 }}>▸</span> Evidência raw
                        <span style={{ fontSize: 11, color: '#8b8f99' }}>
                            <span style={{ color: C.ml }}>order</span> · <span style={{ color: C.mp }}>payment</span> ·{' '}
                            <span style={{ color: C.ext }}>extrato</span> · <span style={{ color: C.nfe }}>nfe</span> —
                            na{' '}
                            <button
                                type="button"
                                onClick={() => navigate(`/payments/order/${orderId}`)}
                                style={{
                                    border: 'none',
                                    background: 'none',
                                    color: '#2563eb',
                                    cursor: 'pointer',
                                    padding: 0,
                                    fontSize: 11,
                                }}
                            >
                                página atual
                            </button>
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
}
